import get from 'lodash/get'
import isArray from 'lodash/isArray'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import orderBy from 'lodash/orderBy'
import sum from 'lodash/sum'
import pluralize from 'pluralize'

import { AND_SUFFIX } from 'const/filters'
import type { FilterOption } from 'types'

/** Options with optional label, for boolean/complex filters (e.g. BooleanFilterOption) */
interface FilterOptionLike {
    value: string | number
    label?: string
}

// Internal type for building boolean filter output (dynamic keys)
interface BooleanFilterOutput {
    and?: Array<{ or: Array<Record<string, string | number>> }>
    [key: string]:
        | string
        | Array<{ or: Array<Record<string, string | number>> }>
        | undefined
}

interface BooleanFilterLeaf {
    resourceType?: string
    operator?: string
    value: string | string[]
}

interface BooleanFilterOr {
    or?: BooleanFilterLeaf[]
}

export interface BooleanFilterValue {
    and?: BooleanFilterOr[]
}

/**
 * Checks if all filter options are selected
 */
export const hasAllOptionsSelected = (
    selectedOptions: FilterOption[],
    allOptions: FilterOption[],
    key = 'value'
): boolean =>
    isEqual(orderBy(selectedOptions, [key]), orderBy(allOptions, [key]))

/**
 * Returns a number or a string of comma separated values
 */
const resolveFilterValue = (value: number | unknown[]): number | string => {
    return isArray(value) ? value.join() : value
}

/**
 * @deprecated Use _compileBooleanFilter_ instead.
 * @see compileBooleanFilter
 */
export function buildBooleanFilter(
    filters: FilterOptionLike[],
    defaultResourceType: string
): BooleanFilterOutput | Record<string, string> {
    const filter = filters[0]
    if (!filter) {
        return { and: [] }
    }

    // Backwards compatibility with the old format
    if (filter.label) {
        const filterValue = filters.map((f) => f.value)
        const resolved = resolveFilterValue(filterValue)
        return {
            [`${defaultResourceType}${AND_SUFFIX}`]:
                typeof resolved === 'string' ? resolved : String(resolved),
        }
    }

    const filterVal = JSON.parse(String(filter.value)) as BooleanFilterValue
    const andClauses = filterVal.and ?? []
    return {
        and: andClauses.map((outerItem) => ({
            or: (outerItem.or ?? []).map((innerItem) => {
                const val = innerItem.value
                const resolved = resolveFilterValue(
                    Array.isArray(val) ? val : [val]
                )
                return {
                    [`${innerItem.resourceType ?? ''}${innerItem.operator ?? ''}`]:
                        typeof resolved === 'number'
                            ? String(resolved)
                            : resolved,
                }
            }),
        })),
    }
}

export const addToComplexTextFilter = (
    currentTextFilter: BooleanFilterValue | null | undefined,
    newPart: FilterOptionLike[]
): BooleanFilterValue => {
    const newFilter: BooleanFilterValue = { and: [] }
    if (currentTextFilter?.and?.length) {
        newFilter.and = [...currentTextFilter.and]
    }
    const newBooleanFilter = buildBooleanFilter(newPart, '')
    const andClauses = newBooleanFilter.and
    if (andClauses?.length) {
        newFilter.and!.push(
            ...((andClauses as unknown as BooleanFilterValue['and']) ?? [])
        )
    }
    return newFilter
}

export const getBooleanFilterLabel = (
    currentValue: BooleanFilterValue | null | undefined,
    labelFormatter:
        | ((resourceType: string, operator: string, value: unknown) => string)
        | null
        | undefined,
    resourceName: string,
    entityText = 'Rule',
    useEntityTextOnSingleRule = false
): string => {
    if (!currentValue) {
        return resourceName
    }
    if (
        currentValue.and?.length === 1 &&
        currentValue.and[0].or?.length === 1
    ) {
        if (labelFormatter) {
            const {
                operator,
                resourceType,
                value: labelValue,
            } = currentValue.and[0].or[0]

            return labelFormatter(
                resourceType ?? '',
                operator ?? '',
                labelValue
            )
        }

        const count = sum(
            (currentValue.and || []).flatMap((and) =>
                (and.or || []).flatMap((leaf) =>
                    isArray(leaf.value) ? leaf.value.length : 1
                )
            )
        )
        return `${pluralize(resourceName, count)}: ${count} ${
            useEntityTextOnSingleRule ? pluralize(entityText, count) : ''
        }`
    }

    const ruleCount = sum(
        (currentValue.and || []).flatMap((and) => (and.or || []).length)
    )

    return `${pluralize(resourceName, ruleCount)}: ${ruleCount} ${pluralize(
        entityText,
        ruleCount
    )}`
}

export function formatBooleanFilter(
    filters: FilterOptionLike[] | undefined,
    defaultResourceType = ''
): string {
    if (!filters?.length) {
        return JSON.stringify({ and: [] })
    }
    return JSON.stringify(buildBooleanFilter(filters, defaultResourceType))
}

/**
 * Transforms dates filter from Cerebro to use in Forge
 */
export function serializeDatesFilter(
    filterValue: { '0'?: { value?: string } } | string
): string | string[] {
    const dates =
        typeof filterValue === 'string'
            ? filterValue
            : get(filterValue, ['0', 'value'], '')
    const datesArray = dates.split(',')
    return datesArray.length === 1 ? datesArray[0] : datesArray
}

/**
 * Formats dates filter to be saved in Cerebro
 */
export function deserializeDatesFilter(
    filterValue: string | string[]
): { value: string }[] {
    if (isArray(filterValue)) {
        return [
            {
                value: isEmpty(filterValue) ? '' : filterValue.join(','),
            },
        ]
    }
    return [{ value: filterValue }]
}
